// @ts-check
const SEC_IN_MINUTE = 60;
const HRS_IN_DAY = 24;
const HRS_IN_WEEK = HRS_IN_DAY * 7;
const HRS_IN_MONTH = HRS_IN_WEEK * 4;
const MS_IN_SECOND = 1000;
const MS_IN_MINUTE = SEC_IN_MINUTE * MS_IN_SECOND;
const MS_IN_HOUR = SEC_IN_MINUTE * MS_IN_MINUTE;
const MS_IN_DAY = HRS_IN_DAY * MS_IN_HOUR;
const ROLE_DISCO_DEVELOPER = 'ROLE_DISCO_DEVELOPER';
const ROLE_DISCO_CSM = 'ROLE_DISCO_CSM';
const ROLE_DISCO_BOX_ADMIN = 'ROLE_DISCO_BOX_ADMIN';
const ROLE_FIRMWARE = 'ROLE_FIRMWARE';
const ROLE_DISCO_BOX_CONFIG_ADMIN = 'ROLE_DISCO_BOX_CONFIG_ADMIN';
const ROLE_SCRIPT = 'ROLE_SCRIPT';

const CLOCK_SKEW_THRESHOLD = 10 * MS_IN_SECOND; // 10 seconds in milliseconds
const MAX_ERROR_MESSAGE_LENGTH = 1000; // Maximum error message length before truncation

const DEPRECATED_EVENT_TYPES = ['battery-health-change', 'battery-health', 'developer-in-production', 'error-counts'];
const SKIP_ERRORS_EXCLUDED_TYPES = ['error', 'error-counts', 'network-traffic-stats'];
// Alert and failure types: the Engineering category, hidden from History, hidden when empty.
// warn-alert is matched exactly so no other type containing "warn" is swept in.
const FAILURE_EVENT_TYPE_REGEX = 'error|failure|^warn-alert$';
const MEANINGFUL_EVENT_DATA_MONGO_CLAUSES = [
  { message: { $regex: /\S/ } },
  { 'payload.message': { $regex: /\S/ } },
  { 'payload.error': { $regex: /\S/ } },
  { 'payload.reason': { $regex: /\S/ } },
  { 'meta.message': { $regex: /\S/ } },
  { 'meta.error': { $regex: /\S/ } },
  { 'meta.reason': { $regex: /\S/ } },
  { $expr: { $gt: [{ $size: { $objectToArray: { $ifNull: ['$payload', {}] } } }, 0] } },
];
const CATEGORY_EVENT_TYPES = {
  engineering: ['error', 'failures-change', 'download-speed-test-failed', 'warn-alert'],
  content: ['content-change'],
  events: [
    'online-status-change',
    'charging-mode-change',
    'hardware-revision-change',
    'hardware-test',
    'hardware-test-change',
    'hardware-version-change',
    'location-change',
    'mac-address-change',
    'new-device-created',
    'create-device',
    'new-device',
    'proxy-change',
    'mode-change',
    'online-method-change',
    'platform-app-change',
    'platform-server-change',
    'shutdown',
    'battery-model-change',
    'current-customer-change',
    'current-firmware-change',
    'firmware-change',
    'network-traffic-stats',
  ],
};

/**
 * Resolve DISCO deploy env from NODE_ENV (staging vs production).
 * @param {string} [raw] Optional override for tests; defaults to process.env.NODE_ENV.
 * @returns {'production' | 'staging' | 'development'}
 */
function resolveDiscoMongoEnv(raw) {
  const source = raw != null ? raw : process.env.NODE_ENV;
  const v = String(source || 'development')
    .trim()
    .toLowerCase();
  if (v === 'production' || v === 'prod') return 'production';
  if (v === 'staging' || v === 'stage') return 'staging';
  return 'development';
}

/**
 * Collection name for DISCO-authored Mongo data only (DeviceEvents, heartbeats,
 * contentChangeBaseline). Do not use for aggregation-sourced collections
 * (analytics, aircraftFlightHistory, EngineeringTriageClusters, metrics-*) —
 * those stay on production base names so staging can read shared fleet telemetry.
 *
 * Production keeps the base name; staging and development both append `-staging`
 * (same suffix on purpose — one staging collection for all non-prod DISCO deploys).
 * @param {string} baseName Production collection name.
 * @param {string} [raw] Optional NODE_ENV override for tests.
 * @returns {string}
 */
function mongoCollectionName(baseName, raw) {
  if (resolveDiscoMongoEnv(raw) === 'production') {
    return baseName;
  }
  return `${baseName}-staging`;
}

module.exports = {
  ROLE_DISCO_DEVELOPER,
  ROLE_DISCO_CSM,
  ROLE_DISCO_BOX_ADMIN,
  ROLE_FIRMWARE,
  ROLE_DISCO_BOX_CONFIG_ADMIN,
  ROLE_SCRIPT,
  FASE_SESSION_DURATION_DAYS: 180,
  DISCO_DEFAULT_TABLE_PAGE_SIZE: 25,
  DISCO_DEFAULT_PAGESIZE_OPTIONS: [25, 50, 100],
  DISCO_AIRCRAFT_TABLE_PAGE_SIZE: 25,
  DISCO_CUSTOMERS_TABLE_PAGE_SIZE: 25,
  DISCO_REPORTS_TABLE_PAGE_SIZE: 25,
  DISCO_FIRMWARE_TABLE_PAGE_SIZE: 25,
  DISCO_AIRFICONFIGS_TABLE_PAGE_SIZE: 25,
  SLOW_DOWNLOAD_BYTES_PER_SEC: 25000,
  DEFAULT_MIN_FREE_DISK_SPACE_GB: 100,
  CHILD_PROCESS_EXEC_MAX_BUFFER_BYTES: 512 * 1024 * 1024, // 512 MB
  DELETE_BATCH_SIZE: 5000, // Batch size for bulk delete operations
  PLACEHOLDER_TEXT_VALUE: 'Enter text',
  PLACEHOLDER_DROPDOWN_VALUE: 'Please select',
  PAGE_ID_TO_TABLE_NAME: {
    'devices-overview': 'devicesTable',
    'device-details': 'devicesTable',
    'customers-overview': 'customersTable',
    'customer-details': 'customersTable',
    'aircraft-overview': 'aircraftTable',
    'aircraft-details': 'aircraftTable',
    'reports-overview': 'reportsTable',
    'engineering-events-overview': 'engineeringEventsTable',
    'history-events-overview': 'historyEventsTable',
    'content-events-overview': 'contentEventsTable',
    'firmware-overview': 'firmwareTable',
    'firmware-details': 'firmwareTable',
    'airfiConfigs-overview': 'airfiConfigsTable',
    'airfiConfig-details': 'airfiConfigsTable',
    'ssds-overview': 'ssdsTable',
    'ssd-details': 'ssdsTable',
    'airfiConfigReleases-overview': 'airfiConfigReleasesTable',
    'airfiConfigRelease-details': 'airfiConfigReleasesTable',
    'create-airficonfigrelease': 'airfiConfigReleasesTable',
    'databases-overview': 'databasesTable',
    'databaseDocs-overview': 'databaseDocsTable',
    'databaseDoc-details': 'databaseDocsTable',
    'flightHistory-overview': 'flightHistoryTable',
    'dailyReport-overview': 'dailyReportTable',
  },
  LAST_DAYS_FASE_ONLINE: 3, // used to check if fase box online status is 'healthy'
  NR_RECENT_FLIGHTS: 2, // used to check if aircraft had number of flights during last days online period
  LAST_MINUTES_ONLINE: 5, // used to check if box is online
  CONTENT_SYNC_STUCK_AGE_MIN: 1440, // used to check if box is online
  LAST_SEEN_IN_MAINENANCE_ALLOWED_DAYS: 3,
  DEVICE_MAP_REFRESH_INTERVAL_MIN: 1,
  ONLINE_DEVICES_REFRESH_INTERVAL_MIN: 1,
  DEVICE_DETAILS_REFRESH_INTERVAL_MIN: 1,
  DEFAULT_HEALTH_STATS: {
    ok: 0,
    maintenance: 0,
    halting: 0,
    failing: 0,
    inactive: 0,
  },
  MS_IN_SECOND,
  MS_IN_MINUTE,
  MS_IN_HOUR,
  MS_IN_DAY,
  SEC_IN_MINUTE,
  HRS_IN_DAY,
  HRS_IN_WEEK,
  HRS_IN_MONTH,
  DEFAULT_FIRMWARE_TYPE: 'moon',
  DEFAULT_FIRMWARE_LABEL: 'internal',
  CONTENT_SYNC_COLORS: {
    ready: '#00863f',
    error: '#712424',
    failure: '#e91e1e',
    syncing: '#efc548',
  },
  CONTENT_SYNC_FONT_COLORS: {
    ready: 'white',
    error: 'white',
    failure: 'white',
    syncing: 'black',
  },
  NON_CRITICAL_ERROR_CODES: [1, 2, 6, 8, 10], // these errors normally resolve on their own
  NON_CRITICAL_ERROR_DURATION_HRS: 0.5, // for how long we should consider non critical errors OK
  MAX_ONLINE_PROXIES_PER_ROW: 8, // how many groups of online boxes per row should be displayed on the online devices screen
  MAX_DEVICE_EVENTS: 100, // how many device events to retrieve per device details request by default
  RECORD_HISTORY_EVENT_TYPES: {
    CREATE_DOCUMENT: 'create document',
    UPDATE_DOCUMENT: 'update document',
    DELETE_DOCUMENT: 'delete document',
    CREATE_DATABASE: 'create database',
    DELETE_DATABASE: 'delete database',
    CREATE_BOX_CONFIG: 'create box config',
    UPDATE_BOX_CONFIG: 'update box config',
  },
  ROLES_MAP: {
    device: {
      assignedCustomer: [ROLE_DISCO_BOX_ADMIN],
      assignedAirfiConfigRelease: [ROLE_DISCO_DEVELOPER],
      assignedFirmware: [ROLE_DISCO_BOX_ADMIN, ROLE_DISCO_DEVELOPER],
      marsFirmware: [ROLE_DISCO_BOX_ADMIN, ROLE_DISCO_DEVELOPER],
      moonFirmware: [ROLE_DISCO_BOX_ADMIN, ROLE_DISCO_DEVELOPER],
      assignedLocation: [ROLE_DISCO_BOX_ADMIN],
      factoryBatchNumber: [ROLE_DISCO_BOX_ADMIN],
      emailRecepientsCustomer: [ROLE_DISCO_CSM],
      emailRecepientsInternal: [ROLE_DISCO_CSM, ROLE_DISCO_DEVELOPER],
      nickname: [ROLE_DISCO_CSM, ROLE_DISCO_BOX_ADMIN, ROLE_DISCO_DEVELOPER],
      owner: [ROLE_DISCO_CSM],
      pmVersion: [ROLE_DISCO_BOX_ADMIN],
      poNumber: [ROLE_DISCO_BOX_ADMIN],
      position: [ROLE_DISCO_CSM],
      aircraft: [ROLE_DISCO_CSM],
      purpose: [ROLE_DISCO_BOX_ADMIN],
      retirementReason: [ROLE_DISCO_BOX_ADMIN],
      operationalStatus: [ROLE_DISCO_BOX_ADMIN],
      isSwTestPassed: [ROLE_FIRMWARE],
      isHwTestPassed: [ROLE_FIRMWARE],
      specialConfig: [ROLE_DISCO_BOX_CONFIG_ADMIN],
      simCardNumber: [ROLE_DISCO_BOX_ADMIN],
      mobileNetworkOperator: [ROLE_DISCO_BOX_ADMIN],
      snoozed: [ROLE_DISCO_BOX_ADMIN],
      powmanVersion: [ROLE_SCRIPT],
    },
    firmware: {
      label: [ROLE_DISCO_BOX_ADMIN, ROLE_DISCO_DEVELOPER],
      disabled: [ROLE_DISCO_BOX_ADMIN, ROLE_DISCO_DEVELOPER],
    },
    airfiConfigRelease: {
      releaseNotes: [ROLE_DISCO_BOX_ADMIN, ROLE_DISCO_DEVELOPER],
    },
    airfiConfig: {
      configString: [ROLE_DISCO_BOX_CONFIG_ADMIN],
    },
    customer: {
      name: [ROLE_DISCO_CSM],
      favicon: [ROLE_DISCO_CSM, ROLE_DISCO_DEVELOPER],
      industry: [ROLE_DISCO_CSM],
      logoOpaqueUrl: [ROLE_DISCO_CSM],
      logoUrl: [ROLE_DISCO_CSM],
      primaryColor: [ROLE_DISCO_CSM],
      secondaryColor: [ROLE_DISCO_CSM],
      websiteUrl: [ROLE_DISCO_CSM],
      status: [ROLE_DISCO_CSM],
    },
    aircraft: {
      hidden: [ROLE_DISCO_CSM],
      customer: [ROLE_DISCO_CSM],
      position: [ROLE_DISCO_CSM],
      hexCode: [ROLE_DISCO_CSM],
    },
  },
  FAILURES_TABS: [
    { name: 'Content Sync', id: 'contentSync' },
    { name: 'Engineering Triage', id: 'engineeringTriage', isNew: true },
  ],
  USER_EVENT_HISTORY_DB_SELECT_LIMIT: 5000,
  SEARCH_MAX_ITEMS: 25,
  HEALTHY_BATTERY_LIFE_SECONDS: 16 * 3600, // 16 hours
  HEALTHY_BATTERY_CHARGE_SECONDS: 8 * 3600, // 8 hours
  DEVICE_AUTO_SNOOZE_DAYS: 50,
  PAGE_VISIT_LOOKBACK_DAYS: 7,
  CLOCK_SKEW_THRESHOLD,
  MAP_OPENED_NAME_TO_ENTITY_NAME: {
    openedDevice: 'device',
    openedAircraft: 'aircraft',
    openedCustomer: 'customer',
    openedAirfiConfig: 'airfiConfig',
    openedFirmware: 'firmware',
    openedAirfiConfigRelease: 'airfiConfigRelease',
  },
  MAX_ERROR_MESSAGE_LENGTH,
  KNOWN_TYPES: ['moon', 'mars', 'superproxy', 'hubproxy-2.0'],
  COLLECTION: {
    ANALYTICS: 'analytics',
    AIRCRAFT_FLIGHT_HISTORY: 'aircraftFlightHistory',
    CONTENT_CHANGE_BASELINE: 'contentChangeBaseline',
    DEVICE_EVENTS: 'DeviceEvents',
    ENGINEERING_TRIAGE_CLUSTERS: 'EngineeringTriageClusters',
    HEARTBEATS: 'heartbeats',
  },
  DB: {
    BOX_METRICS: 'box-metrics',
  },
  resolveDiscoMongoEnv,
  mongoCollectionName,
  DEPRECATED_EVENT_TYPES,
  SKIP_ERRORS_EXCLUDED_TYPES,
  EVENT_CATEGORIES: {
    ALL: 'all',
    ENGINEERING: 'engineering',
    HISTORY: 'history',
    CONTENT: 'content',
    EVENTS: 'events',
  },
  CATEGORY_EVENT_TYPES,
  isDeprecatedEventType(type) {
    return DEPRECATED_EVENT_TYPES.includes(type);
  },
  hasEventDisplayContent(row) {
    if (!row) return false;
    const candidates = [
      row.message,
      row.payload?.message,
      row.payload?.error,
      row.payload?.reason,
      row.meta?.message,
      row.meta?.error,
      row.meta?.reason,
    ];
    if (candidates.some((value) => typeof value === 'string' && value.trim())) {
      return true;
    }
    if (row.payload && typeof row.payload === 'object' && Object.keys(row.payload).length > 0) {
      return true;
    }
    if (row.meta && typeof row.meta === 'object' && Object.keys(row.meta).length > 0) {
      return true;
    }
    return false;
  },
  isFailureEventType(type) {
    return Boolean(type && new RegExp(FAILURE_EVENT_TYPE_REGEX, 'i').test(String(type)));
  },
  isDisplayableEventRow(row) {
    if (!row || !row.type) return false;
    if (module.exports.isDeprecatedEventType(row.type)) return false;
    if (module.exports.hasEventDisplayContent(row)) return true;
    if (module.exports.isFailureEventType(row.type)) return false;
    return true;
  },
  getFailureEventVisibilityFilter() {
    return {
      $or: [
        { type: { $not: { $regex: FAILURE_EVENT_TYPE_REGEX, $options: 'i' } } },
        ...MEANINGFUL_EVENT_DATA_MONGO_CLAUSES,
      ],
    };
  },
  getEventCategory(type) {
    if (!type) return module.exports.EVENT_CATEGORIES.HISTORY;
    if (module.exports.isDeprecatedEventType(type)) return null;
    if (CATEGORY_EVENT_TYPES.content.includes(type)) {
      return module.exports.EVENT_CATEGORIES.CONTENT;
    }
    if (CATEGORY_EVENT_TYPES.engineering.includes(type) || new RegExp(FAILURE_EVENT_TYPE_REGEX, 'i').test(type)) {
      return module.exports.EVENT_CATEGORIES.ENGINEERING;
    }
    if (CATEGORY_EVENT_TYPES.events.includes(type)) {
      return module.exports.EVENT_CATEGORIES.EVENTS;
    }
    return module.exports.EVENT_CATEGORIES.HISTORY;
  },
  resolveTypesForCategory(category, distinctTypes) {
    if (!category || !Array.isArray(distinctTypes)) {
      return [];
    }
    if (category === module.exports.EVENT_CATEGORIES.ALL) {
      return distinctTypes.filter((type) => !module.exports.isDeprecatedEventType(type));
    }
    return distinctTypes.filter((type) => module.exports.getEventCategory(type) === category);
  },
  getFilterForCategory(category) {
    const eventsTypes = CATEGORY_EVENT_TYPES.events;
    if (category === module.exports.EVENT_CATEGORIES.ALL) {
      return {
        type: { $nin: DEPRECATED_EVENT_TYPES },
      };
    }
    if (category === 'engineering') {
      return {
        $and: [
          {
            $or: [
              { type: { $in: CATEGORY_EVENT_TYPES.engineering } },
              { type: { $regex: FAILURE_EVENT_TYPE_REGEX, $options: 'i' } },
            ],
          },
          { type: { $nin: DEPRECATED_EVENT_TYPES } },
        ],
      };
    }
    if (category === 'content') {
      return {
        type: 'content-change',
      };
    }
    if (category === 'events') {
      return {
        type: {
          $in: eventsTypes,
        },
      };
    }
    if (category === 'history') {
      // Use $and — $nin + $not on the same field object is unreliable in MongoDB.
      return {
        $and: [
          { type: { $ne: 'content-change' } },
          { type: { $nin: [...eventsTypes, ...DEPRECATED_EVENT_TYPES] } },
          { type: { $not: { $regex: FAILURE_EVENT_TYPE_REGEX, $options: 'i' } } },
        ],
      };
    }
    return {};
  },
};
